import { brandsRead } from '@zealot/shared/apiClient';
import { createClient } from '@zealot/shared/apiClient/client';
import { authFetch } from '@/lib/auth/authFetch';
import { API_BASE_URL, NEW_API_URL } from '@/utils/api';
import logger from '@/utils/logger';

const API_URL = `${API_BASE_URL}/brand-missions`;

/**
 * List missions for public display (no auth required)
 * Uses the new Hono API with brandID query param
 */
async function listPublicMissions(brandID) {
  try {
    const baseUrl = NEW_API_URL || 'http://localhost:8000';
    const params = new URLSearchParams({ brandID: String(brandID), status: 'active' });
    const response = await authFetch(`${baseUrl}/api/v2/brandMissions?${params}`);
    const data = await response.json();
    return {
      statusCode: response.status,
      body: data,
    };
  } catch (_error) {
    logger.error(_error);
    return {
      statusCode: 500,
      body: { error: 'Failed to list public missions' },
    };
  }
}

// gloss
const BRAND_LABEL_LOOKUPS = ['brandName', 'code'];

// why: a plain client, because authFetch stamps x-brand-uuid and the API answers 403 to a lookup for another brand.
const readBrandByLabel = async (brandLabel) => {
  const client = createClient({ baseUrl: NEW_API_URL || 'http://localhost:8000' });

  for (const lookup of BRAND_LABEL_LOOKUPS) {
    const { data, error, response } = await brandsRead({ client, path: { uuid: brandLabel }, query: { lookup } });

    if (response?.status === 404) continue;

    if (error || !data?.data) {
      logger.warn('readBrandByLabel error response', { status: response?.status, lookup });
      return { statusCode: response?.status ?? 500, brand: undefined };
    }

    return { statusCode: 200, brand: data.data };
  }

  return { statusCode: 200, brand: undefined };
};

// gloss
async function getBrandIDByName(brandName) {
  if (!brandName) {
    return { statusCode: 200, body: [] };
  }

  try {
    const { statusCode, brand } = await readBrandByLabel(brandName);

    if (statusCode !== 200) {
      return { statusCode, body: JSON.stringify({ error: 'Failed to load brand id' }) };
    }

    return { statusCode: 200, body: brand ? [{ brandID: Number(brand.brandID) }] : [] };
  } catch (error) {
    logger.error(error);
    return {
      statusCode: 500,
      body: JSON.stringify({ error: 'Failed to load brand id' }),
    };
  }
}

async function getTestimonialMissionQuestions(brandMissionID, accessToken) {
  try {
    const response = await authFetch(`${API_URL}/testimonialMissionQuestions/${brandMissionID}`, {
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
    });
    const data = await response.json();
    return {
      statusCode: 200,
      body: data,
    };
  } catch (_error) {
    return {
      statusCode: 500,
      body: JSON.stringify({
        error: 'Failed to get testimonial mission questions',
      }),
    };
  }
}

async function listBrandMissions(_action, userID, brandID, accessToken) {
  try {
    const response = await authFetch(`${API_URL}/listBrandMissions/${brandID}/${userID}/fan`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const data = await response.json();
    return data;
  } catch (_error) {
    return {
      statusCode: 500,
      body: JSON.stringify({ error: 'Failed to list brand missions' }),
    };
  }
}

async function getGeneralMissions(_action, brandID, userID, accessToken) {
  try {
    const response = await authFetch(`${API_URL}/listBrandMissions/${brandID}/${userID}/fan`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const data = await response.json();
    return data;
  } catch (_error) {
    return {
      statusCode: 500,
      body: JSON.stringify({ error: 'Failed to list brand missions' }),
    };
  }
}

async function getBrandCodeByBrandName(brandName, accessToken) {
  try {
    const response = await authFetch(`${API_URL}/getBrandCodeByBrandName/${brandName}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const data = await response.json();
    return {
      statusCode: 200,
      body: data,
    };
  } catch (_error) {
    return {
      statusCode: 500,
      body: JSON.stringify({ error: 'Failed to get brand code by brand name' }),
    };
  }
}

export {
  getBrandCodeByBrandName,
  getBrandIDByName,
  getGeneralMissions,
  getTestimonialMissionQuestions,
  listBrandMissions,
  listPublicMissions,
  readBrandByLabel,
};
